import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Users,
  Activity,
  Shield,
  Settings,
  LogOut,
  User,
  Leaf,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  SidebarProvider,
} from "../components/ui/sidebar";
import { Sheet, SheetContent } from "../components/ui/sheet";
import { useIsMobile } from "../hooks/use-mobile";

const Layout = ({ children, navigationItems, currentPath }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isMobile = useIsMobile();
  const { user, logout, isSuperAdmin, hasMinimumRole } = useAuth();
  const navigate = useNavigate();

  // Filter navigation items based on user role
  const filteredNavigationItems = navigationItems.filter((item) => {
    // Hide super admin only pages for non-super admins
    if (item.superAdminOnly && !isSuperAdmin()) {
      return false;
    }
    // Hide admin only pages for non-admins (salesperson role)
    if (item.adminOnly && !hasMinimumRole("admin")) {
      return false;
    }
    return true;
  });

  const handleLogout = () => {
    logout();
  };

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleNavigationClick = (path) => {
    navigate(path);
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const SidebarContentComponent = () => (
    <>
      <SidebarHeader className="border-b border-gray-100 p-4 lg:p-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/20">
            <Leaf className="w-4 h-4 lg:w-6 lg:h-6 text-white" />
          </div>
          <div className="hidden lg:block">
            <h2 className="font-bold text-lg lg:text-xl text-gray-900">
              Ardorio CRM
            </h2>
            <p className="text-xs text-gray-500">Lead Management</p>
          </div>
          <div className="lg:hidden">
            <h2 className="font-bold text-lg text-gray-900">Ardorio</h2>
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent className="p-2 lg:p-3">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1">
              {filteredNavigationItems.map((item) => {
                const isActive = currentPath === item.path;
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      className={`transition-all duration-300 rounded-xl ${
                        isActive
                          ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30"
                          : "hover:bg-gray-50 text-gray-700"
                      }`}
                      onClick={() => handleNavigationClick(item.path)}
                    >
                      <div className="flex items-center gap-2 lg:gap-3 px-3 py-2 lg:px-4 lg:py-3 cursor-pointer">
                        <item.icon className="w-4 h-4 lg:w-5 lg:h-5" />
                        <span className="font-medium text-sm lg:text-base">
                          {item.title}
                        </span>
                      </div>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-2 lg:p-4 border-t border-gray-100">
        <div className="space-y-2 lg:space-y-3">
          <div className="flex items-center gap-2 lg:gap-3 px-2 lg:px-3 py-2 bg-gray-50 rounded-xl">
            <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm lg:text-base">
              {user?.name?.charAt(0)?.toUpperCase() || (
                <User className="w-4 h-4 lg:w-5 lg:h-5" />
              )}
            </div>
            <div className="flex-1 min-w-0 hidden lg:block">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.name || "User"}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {user?.email || ""}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 lg:gap-3 px-3 lg:px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-xl transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden lg:inline">Sign Out</span>
            <span className="lg:hidden">Logout</span>
          </button>
        </div>
      </SidebarFooter>
    </>
  );

  return (
    <SidebarProvider>
      <style>
        {`
          :root {
            --primary: #00C68D;
            --primary-hover: #01C891;
            --background: #FFFFFF;
            --card-bg: rgba(255, 255, 255, 0.8);
            --text-primary: #1a1a1a;
            --text-secondary: #6b7280;
            --border: #e5e7eb;
          }
          
          /* Prevent horizontal scrolling on mobile */
          body {
            overflow-x: hidden;
          }
          
          /* Ensure proper mobile layout */
          @media (max-width: 768px) {
            .min-h-screen {
              min-height: 100vh;
              min-height: 100dvh; /* Use dynamic viewport height for mobile */
            }
          }
        `}
      </style>
      <div className="min-h-screen flex w-full bg-gradient-to-br from-gray-50 via-white to-blue-50/20">
        {/* Mobile Sidebar (Sheet) */}
        {isMobile ? (
          <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
            <SheetContent side="left" className="w-80 p-0">
              <div className="flex h-full flex-col bg-white/80 backdrop-blur-xl">
                <SidebarContentComponent />
              </div>
            </SheetContent>
          </Sheet>
        ) : (
          /* Desktop Sidebar */
          <Sidebar className="border-r border-gray-100 bg-white/80 backdrop-blur-xl">
            <SidebarContentComponent />
          </Sidebar>
        )}

        <main className="flex-1 flex flex-col">
          {isMobile && (
            <header className="bg-white/60 backdrop-blur-xl border-b border-gray-100 px-4 py-3 sticky top-0 z-50">
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSidebarToggle}
                  className="hover:bg-gray-100 p-2 rounded-lg transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                </button>
                <h1 className="text-lg font-bold">Ardorio CRM</h1>
              </div>
            </header>
          )}
          <div className="flex-1 overflow-auto p-2 lg:p-0 w-full min-w-0">
            {children}
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default Layout;
